/**
 * countdown.ts — shared, dependency-free time-math for the pageant
 * countdown (public-site-countdown-banner spec, task 1.1).
 *
 * This module is the single source of truth the portal
 * `CountdownBanner` and the public `PublicCountdownBanner` both import,
 * so the two surfaces derive identical numbers from the same anchor and
 * reference time (Requirements 4.1–4.5, 6.3, 9.3). The four helpers
 * `parseTime`, `deriveTargetMs`, `formatAnchorDate`, and the inline
 * `diff → D/H/M/S` math were previously module-local to
 * `frontend/src/components/shared/CountdownBanner.tsx`; their behavior is
 * copied here verbatim so the portal refactor is semantically a no-op.
 *
 * The added `computeRemaining` and `resolveState` helpers are pure and
 * React-free, so they are unit- and property-testable in isolation.
 * `resolveState` mirrors the cache-fallback resolver already proven in
 * the mobile `useCountdown` (`resolveState`) logic.
 *
 * Requirements: 1.3, 1.5, 1.7, 1.8, 2.4, 2.5, 2.6, 3.2, 3.3,
 *               4.1, 4.2, 4.3, 4.5
 */

/** Minimal anchor shape the time-math needs (subset of CalendarEvent). */
export interface AnchorLike {
  date: string;
  time: string;
  allDay: boolean;
}

/** Remaining-time breakdown. */
export interface Remaining {
  days: number; // 0 or greater
  hours: number; // 0..23
  minutes: number; // 0..59
  seconds: number; // 0..59
}

/**
 * Normalize a human time string ("7:00 PM", "19:00", "") into a
 * 24-hour `HH:MM:SS` string suitable for concatenation onto an ISO
 * date. Falls back to `00:00:00` for `allDay` events or empty /
 * whitespace / unparseable input rather than throwing — the banner
 * must keep rendering even when calendar data is dirty
 * (Requirements 4.2, 4.3).
 */
export function parseTime(time: string, allDay: boolean): string {
  if (allDay) return '00:00:00';
  const trimmed = (time ?? '').trim();
  if (!trimmed) return '00:00:00';
  const probe = new Date(`1970-01-01 ${trimmed}`);
  if (Number.isNaN(probe.getTime())) return '00:00:00';
  return probe.toTimeString().slice(0, 8);
}

/**
 * Build the absolute target millisecond timestamp from an anchor by
 * combining `date` + parsed `time` in the browser's local timezone.
 * Returns `null` when the anchor is absent or its `date` is missing /
 * unparseable (Requirements 4.1, 4.2, 4.3, 4.5).
 */
export function deriveTargetMs(anchor: AnchorLike | null): number | null {
  if (!anchor || !anchor.date) return null;
  const clock = parseTime(anchor.time, anchor.allDay);
  const ms = new Date(`${anchor.date}T${clock}`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Long-form date subtitle derived from `anchor.date`, never hardcoded
 * (Requirement 1.5). Format mirrors the long-form date readout used by
 * the mobile surface (e.g. "October 22, 2026"). Returns `""` for an
 * unparseable date.
 */
export function formatAnchorDate(dateIso: string): string {
  const d = new Date(`${dateIso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Split a positive remaining millisecond delta into D/H/M/S, mirroring
 * the existing inline portal math exactly. Callers must only invoke
 * this when `targetMs - nowMs > 0`; the past/zero case is handled by the
 * No_Anchor_State branch (Requirements 1.3, 1.8, 3.2).
 */
export function computeRemaining(targetMs: number, nowMs: number): Remaining {
  const diff = targetMs - nowMs;
  const totalSeconds = Math.floor(diff / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

/** Hook-derived inputs the resolver decides from. */
export interface ResolveStateInput {
  loading: boolean;
  error: Error | null;
  anchor: AnchorLike | null;
}

/**
 * Result of {@link resolveState}: the populated/no-anchor decision plus
 * the active target. `targetMs` doubles as the new cache value the
 * caller should write back into its last-good-target ref.
 */
export interface ResolvedState {
  state: 'populated' | 'no-anchor';
  targetMs: number | null;
}

/**
 * Pure cache-fallback resolver. Given the current hook state, the
 * reference `now`, and the last-good target the caller has cached,
 * decide the active target and whether to render the Populated or
 * No_Anchor state:
 *
 *   - When `deriveTargetMs(anchor)` is non-null, use it (and the caller
 *     updates its cache to this value).
 *   - Else while `loading || error`, fall back to the cached last-good
 *     target (which may itself be `null`).
 *   - Else (authoritative null — settled with no anchor), clear the
 *     cache and resolve `null`.
 *
 * The result is `no-anchor` whenever the active target is `null` or at
 * or before `now`; otherwise `populated`. The returned `targetMs` is the
 * active target, which the caller writes back as the new cached value.
 *
 * Mirrors the mobile `useCountdown` `resolveState` logic so both web and
 * mobile surfaces share the same fallback semantics
 * (Requirements 1.7, 1.8, 2.4, 2.5, 2.6, 3.2, 3.3, 4.5).
 */
export function resolveState(
  input: ResolveStateInput,
  now: number,
  cachedTargetMs: number | null,
): ResolvedState {
  const { loading, error, anchor } = input;
  let activeTargetMs: number | null;
  const derived = deriveTargetMs(anchor);
  if (derived !== null) {
    activeTargetMs = derived;
  } else if (loading || error) {
    activeTargetMs = cachedTargetMs;
  } else {
    activeTargetMs = null;
  }

  if (activeTargetMs === null || activeTargetMs - now <= 0) {
    return { state: 'no-anchor', targetMs: activeTargetMs };
  }
  return { state: 'populated', targetMs: activeTargetMs };
}
